import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { Installation } from 'src/Libs/installations/models/installation.model'
import AddressBlock from './AddressBlock'
import { ChangeLocationButton, DeleteInstallationButton } from './InstallationActions'
import { getCompactTableSx } from './table-styles'

type InformationTableRow = {
  installationId: string
  address: React.ReactNode
  deleteInstallation: React.ReactNode
  changeLocation: React.ReactNode
}

interface InstallationInformationTableProps {
  installation: Installation | null | undefined
  installationId: string | undefined
  isInstallationDecommissioned: boolean
  refetchInstallation: () => void
}

/**
 * Table with the general installation information (id, address) and the installation actions (delete, change location)
 * @param installation - installation
 * @param installationId - installation id
 * @param isInstallationDecommissioned - whether the installation is decommissioned, the actions are not available then
 * @param refetchInstallation - function to refetch the installation data
 */
const InstallationInformationTable: React.FC<InstallationInformationTableProps> = ({
  installation,
  installationId,
  isInstallationDecommissioned,
  refetchInstallation,
}) => {
  const { t } = useCustomTranslation()

  const installationAddress = useMemo(() => {
    const hasInstallationAddress = [
      installation?.streetName,
      installation?.streetNumber,
      installation?.zip,
      installation?.city,
      installation?.countryCode,
    ].some(addressElement => !!addressElement)

    if (!hasInstallationAddress) {
      return null
    }

    return {
      street: [`${installation?.streetName || ''} `, `${installation?.streetNumber || ''}`].join(''),
      city: [`${installation?.zip || ''} `, `${installation?.city || ''}`].join(''),
      country: installation?.countryCode ? t(`common.countries.short.${installation?.countryCode}`) : undefined,
    }
  }, [installation]) //eslint-disable-line

  const columns: Header<InformationTableRow>[] = [
    {
      value: 'installationId',
      label: t('installation.details.info.installation-id'),
      isSortable: false,
    },
    {
      value: 'address',
      label: t('installation.details.info.installation-address'),
      isSortable: false,
    },
    {
      value: 'deleteInstallation',
      label: t('system-details.components.delete-installation.delete'),
      isSortable: false,
    },
    {
      value: 'changeLocation',
      label: t('installation-details.change-location.action.title'),
      isSortable: false,
    },
  ]

  const canUseActions = !!installation && !isInstallationDecommissioned

  const tableData: InformationTableRow[] = [
    {
      installationId: installationId ?? '-',
      address: installationAddress ? (
        <AddressBlock street={installationAddress.street} city={installationAddress.city} country={installationAddress.country} />
      ) : (
        '-'
      ),
      deleteInstallation: canUseActions ? <DeleteInstallationButton installation={installation} /> : '-',
      changeLocation: canUseActions ? <ChangeLocationButton installation={installation} refetchInstallation={refetchInstallation} /> : '-',
    },
  ]

  return (
    <Box sx={getCompactTableSx(-2)}>
      <TableComplete
        header={columns}
        data={tableData}
        headerElevation={0}
        bodyElevation={0}
        footerElevation={0}
        disablePagination
        allowOtherCellColors
      />
    </Box>
  )
}

export default InstallationInformationTable
